import { Link } from 'react-router-dom'
import { Newspaper, ArrowRight, Loader2, AlertCircle, Star } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import { useNews, formatNewsDate, newsTagLabel, type NewsItem } from '@/hooks/useNews'
import { useLanguage } from '@/localization'

export default function NewsPage() {
  const state = useNews()
  const { t } = useLanguage()

  return (
    <>
      <PageHeader
        title="News"
        subtitle="Server updates, events and announcements for the Uliunai Minecraft community."
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {state.status === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-deep/60">
            <Loader2 className="animate-spin" size={20} />
            {t('Loading the latest updates…')}
          </div>
        )}

        {state.status === 'error' && (
          <div className="mx-auto flex max-w-lg items-center gap-3 rounded-xl border border-stone-light/60 bg-white p-5 text-slate-deep/70 shadow-block">
            <AlertCircle className="text-gold-dark shrink-0" size={22} />
            <span>{t('News is taking a break right now — check back soon for the latest Minecraft updates.')}</span>
          </div>
        )}

        {state.status === 'empty' && (
          <div className="mx-auto flex max-w-lg items-center gap-3 rounded-xl border border-stone-light/60 bg-white p-5 text-slate-deep/70 shadow-block">
            <Newspaper className="text-grass-dark shrink-0" size={22} />
            <span>{t('No news yet — check back here for vanilla updates and community events.')}</span>
          </div>
        )}

        {state.status === 'ready' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {state.items.map((item) => (
              <NewsListCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function NewsListCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage()
  const date = formatNewsDate(item.date)
  return (
    <Link
      to={`/news/${item.id}`}
      className="group relative flex flex-col rounded-2xl border border-stone-light/60 bg-white p-5 shadow-block transition-colors hover:border-grass-dark/60"
    >
      {item.pinned && (
        <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-md bg-gold/20 px-2 py-1 font-pixel text-[8px] uppercase tracking-[0.15em] text-gold-dark">
          <Star size={10} /> {t('Pinned')}
        </span>
      )}
      {item.tag && (
        <span className="mb-2 inline-flex w-fit rounded-md bg-grass/15 px-2 py-1 font-pixel text-[8px] uppercase tracking-[0.15em] text-grass-deep">
          {t(newsTagLabel(item.tag))}
        </span>
      )}
      {date && <span className="mb-2 text-xs font-bold text-grass-deep">{date}</span>}
      <h3 className="mb-2 pr-16 font-extrabold text-slate-deep transition-colors group-hover:text-grass-deep">
        {item.title}
      </h3>
      {item.excerpt && <p className="line-clamp-3 flex-1 text-sm text-slate-deep/70">{item.excerpt}</p>}
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-grass-deep">
        {t('Read more')} <ArrowRight size={12} />
      </span>
    </Link>
  )
}
